import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '~/services/api';
import useNotificationSound from './useNotificationSound';

export default function useWhatsappSupport() {
  const currentUser = useSelector(({ user }) => user.profile);

  const [queue, setQueue] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const { notifyNewQueueItem, notifyNewMessage } = useNotificationSound();

  // Ref para manter activeTicket atualizado dentro dos callbacks do Pusher
  const activeTicketRef = useRef(null);
  activeTicketRef.current = activeTicket;

  // Load Inicial
  const loadQueue = useCallback(async () => {
    try {
      const { data } = await api.get('/support/whatsapp/queue');
      setQueue(data.queue || []);
    } catch (err) {
      console.error('Erro ao carregar fila:', err);
    }
  }, []);

  const loadMyTickets = useCallback(async () => {
    try {
      const { data } = await api.get('/support/whatsapp/my-tickets');
      setMyTickets(data.tickets || []);
    } catch (err) {
      console.error('Erro ao carregar meus atendimentos:', err);
    }
  }, []);

  const loadQuickReplies = useCallback(async () => {
    try {
      const { data } = await api.get('/support/whatsapp/quick-replies');
      setQuickReplies(data.quick_replies || []);
    } catch (err) {
      console.error('Erro ao carregar atalhos:', err);
    }
  }, []);

  const loadMessages = useCallback(async (chatId, subscriberSchema) => {
    if (!chatId || !subscriberSchema) return;
    setLoadingMessages(true);
    try {
      const { data } = await api.get(
        `/support/whatsapp/chats/${chatId}/messages`,
        {
          params: {
            subscriber_schema: subscriberSchema,
          },
        }
      );
      const msgs = data.messages || [];
      setMessages(msgs.reverse());
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Action
  const pickUp = useCallback(
    async queueItemId => {
      try {
        const { data } = await api.post(
          `/support/whatsapp/queue/${queueItemId}/pick`
        );

        if (data.success) {
          toast.success('Atendimento iniciado!');
          // Recarregar fila e tickets
          await loadQueue();
          await loadMyTickets();

          // Selecionar o ticket recém-criado
          const newTicket = {
            id: queueItemId,
            chat_id: data.chat_id,
            subscriber_id: data.queue_item?.subscriber_id,
            subscriber_schema: data.queue_item?.subscriber_schema,
            subscriber_name: data.queue_item?.subscriber_name,
            store_name: data.queue_item?.store_name,
            customer_name: data.queue_item?.customer_name,
            customer_phone: data.queue_item?.customer_phone,
            order_number: data.queue_item?.order_number,
            cart_id: data.queue_item?.cart_id,
            ...data.queue_item,
          };
          setActiveTicket(newTicket);
          await loadMessages(data.chat_id, data.queue_item?.subscriber_schema);
        }
      } catch (err) {
        const errorMsg =
          err?.response?.data?.error || 'Erro ao pegar atendimento.';
        toast.error(errorMsg);
      }
    },
    [loadQueue, loadMyTickets, loadMessages]
  );

  const loadRecentTickets = useCallback(async (hours = 48) => {
    try {
      const { data } = await api.get('/support/whatsapp/recent', {
        params: { hours },
      });
      setRecentTickets(data.tickets || []);
    } catch (err) {
      console.error('Erro ao carregar recentes:', err);
    }
  }, []);

  const changeRecentPeriod = useCallback(
    async hours => {
      await loadRecentTickets(Number(hours));
    },
    [loadRecentTickets]
  );

  const sendMessage = useCallback(async (chatId, content, subscriberSchema) => {
    if (!chatId || !content?.trim() || !subscriberSchema) return;
    try {
      const { data } = await api.post(
        `/support/whatsapp/chats/${chatId}/messages`,
        {
          content,
          subscriber_schema: subscriberSchema,
        }
      );
      if (data.message) {
        setMessages(prev => [...prev, data.message]);
      }
    } catch (err) {
      toast.error('Erro ao enviar mensagem.');
    }
  }, []);

  const sendAttachment = useCallback(
    async (chatId, file, caption, subscriberSchema) => {
      if (!chatId || !file || !subscriberSchema) return;
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('subscriber_schema', subscriberSchema);
        if (caption) formData.append('caption', caption);

        const { data } = await api.post(
          `/support/whatsapp/chats/${chatId}/attachment`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        if (data.message) {
          setMessages(prev => [...prev, data.message]);
        }
      } catch (err) {
        toast.error('Erro ao enviar anexo.');
      }
    },
    []
  );

  const transferTicket = useCallback(
    async (queueItemId, newUserId) => {
      try {
        const { data } = await api.post(
          `/support/whatsapp/queue/${queueItemId}/transfer`,
          {
            user_id: newUserId,
          }
        );

        if (data.success) {
          toast.success('Atendimento transferido!');
          setActiveTicket(null);
          setMessages([]);
          await loadQueue();
          await loadMyTickets();
        }
      } catch (err) {
        const errorMsg =
          err?.response?.data?.error || 'Erro ao transferir atendimento.';
        toast.error(errorMsg);
      }
    },
    [loadQueue, loadMyTickets]
  );

  const closeTicket = useCallback(
    async queueItemId => {
      try {
        const { data } = await api.post(
          `/support/whatsapp/queue/${queueItemId}/close`
        );

        if (data.success) {
          toast.success('Atendimento encerrado!');
          setActiveTicket(null);
          setMessages([]);
          await loadQueue();
          await loadMyTickets();
          await loadRecentTickets();
        }
      } catch (err) {
        toast.error('Erro ao encerrar atendimento.');
      }
    },
    [loadQueue, loadMyTickets, loadRecentTickets]
  );

  const reopenTicket = useCallback(
    async queueItemId => {
      try {
        const { data } = await api.post(
          `/support/whatsapp/queue/${queueItemId}/reopen`
        );

        if (data.success) {
          toast.success('Atendimento reaberto!');
          await loadMyTickets();
          await loadRecentTickets();

          const ticket = {
            id: queueItemId,
            chat_id: data.chat_id,
            ...data.queue_item,
          };
          setActiveTicket(ticket);
          if (data.chat_id) {
            await loadMessages(
              data.chat_id,
              data.queue_item?.subscriber_schema
            );
          }
        }
      } catch (err) {
        const errorMsg =
          err?.response?.data?.error || 'Erro ao reabrir atendimento.';
        toast.error(errorMsg);
      }
    },
    [loadMyTickets, loadRecentTickets, loadMessages]
  );

  const resolveQuickReply = useCallback(async (shortcut, queueItemId) => {
    try {
      const { data } = await api.post(
        '/support/whatsapp/quick-replies/resolve',
        {
          shortcut,
          queue_item_id: queueItemId,
        }
      );
      return data.content || '';
    } catch (err) {
      return '';
    }
  }, []);

  const selectTicket = useCallback(
    async ticket => {
      setActiveTicket(ticket);

      // Limpar badge de unread deste ticket
      setMyTickets(prev =>
        prev.map(t => {
          if (t.id === ticket.id) {
            return {
              ...t,
              has_unread: false,
              unread_count: 0,
            };
          }
          return t;
        })
      );

      if (ticket?.chat_id && ticket?.subscriber_schema) {
        await loadMessages(ticket.chat_id, ticket.subscriber_schema);
      }
    },
    [loadMessages]
  );

  // Pusher — Real Time
  useEffect(() => {
    if (!currentUser?.id) return undefined;

    // Atualizar headers do Pusher com token atual e sem X-Server
    try {
      if (window.Echo?.connector?.pusher) {
        window.Echo.connector.pusher.config.auth = {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('@VIDDY:Token')}`,
          },
        };
      }
    } catch (e) {
      // silent
    }

    // Canal da fila de suporte (novos items, picked, transferred, closed)
    const supportChannel = window.Echo.private('support-queue');

    supportChannel.listen('.queue.updated', event => {
      const { action } = event;

      if (action === 'new_item') {
        setQueue(prev => [...prev, event.queueItem]);
        notifyNewQueueItem(event.queueItem?.customer_name);
      }

      if (action === 'picked' || action === 'closed') {
        setQueue(prev => prev.filter(item => item.id !== event.queueItem?.id));
        if (event.queueItem?.assigned_user_id === currentUser.id) {
          loadMyTickets();
        }
      }

      if (action === 'transferred') {
        if (event.queueItem?.assigned_user_id === currentUser.id) {
          loadMyTickets();
          notifyNewQueueItem(event.queueItem?.customer_name);
        }
        loadMyTickets();
      }
    });

    // Canal pessoal do user (mensagens do chat)
    const userChannel = window.Echo.private(`users.${currentUser.id}`);

    userChannel.listen('.MessageCreated', event => {
      const msg = event?.message;
      if (!msg) return;

      const currentTicket = activeTicketRef.current;

      // Se a mensagem é do chat ativo, adicionar ao feed
      if (currentTicket?.chat_id && msg.chat?.id === currentTicket.chat_id) {
        setMessages(prev => {
          const exists = prev.some(m => m.id === msg.id);
          if (exists) return prev;
          return [...prev, msg];
        });
      }

      // Mensagem do cliente via WhatsApp
      if (msg.source === 'whatsapp' && msg.chat?.type === 'whatsapp_support') {
        // Atualizar badge de unread nos meus tickets
        setMyTickets(prev =>
          prev.map(ticket => {
            if (ticket.chat_id === msg.chat?.id) {
              return {
                ...ticket,
                has_unread: currentTicket?.chat_id !== msg.chat?.id,
                unread_count: (ticket.unread_count || 0) + 1,
              };
            }
            return ticket;
          })
        );

        // Notificação sonora + visual se não é o chat ativo
        if (!currentTicket || currentTicket.chat_id !== msg.chat?.id) {
          const senderName = msg.external_author_name || 'Cliente';
          notifyNewMessage(senderName);
        }
      }
    });

    return () => {
      supportChannel.stopListening('.queue.updated');
      userChannel.stopListening('.MessageCreated');
      window.Echo.leave('support-queue');
      window.Echo.leave(`users.${currentUser.id}`);
    };
  }, [currentUser?.id, loadMyTickets, notifyNewQueueItem, notifyNewMessage]);

  // Polling da fila — fallback para quando Pusher não conecta
  useEffect(() => {
    // Se Pusher está conectado, polling a cada 30s (backup leve)
    // Se não, polling a cada 10s (compensar falta de real-time)
    const getPusherState = () => {
      try {
        return window.Echo?.connector?.pusher?.connection?.state;
      } catch (e) {
        return 'unavailable';
      }
    };

    const interval = setInterval(async () => {
      const state = getPusherState();
      const isConnected = state === 'connected';

      // Se Pusher conectado, polling leve a cada 30s
      // O intervalo é 10s, mas pulamos 2 de cada 3 quando conectado
      if (isConnected && Math.random() > 0.33) return;

      try {
        const { data } = await api.get('/support/whatsapp/queue');
        setQueue(prev => {
          const newQueue = data.queue || [];
          if (newQueue.length > prev.length) {
            const newItems = newQueue.filter(
              nq => !prev.some(pq => pq.id === nq.id)
            );
            newItems.forEach(item => {
              notifyNewQueueItem(item.customer_name);
            });
          }
          return newQueue;
        });
      } catch (err) {
        // silent
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [notifyNewQueueItem]);

  // Load Inicial
  useEffect(() => {
    const init = async () => {
      setLoadingQueue(true);
      await Promise.all([
        loadQueue(),
        loadMyTickets(),
        loadQuickReplies(),
        loadRecentTickets(),
      ]);
      setLoadingQueue(false);
    };
    init();
  }, [loadQueue, loadMyTickets, loadQuickReplies, loadRecentTickets]);

  return {
    queue,
    myTickets,
    activeTicket,
    messages,
    loadingQueue,
    loadingMessages,
    quickReplies,
    currentUser,
    pickUp,
    sendMessage,
    transferTicket,
    closeTicket,
    resolveQuickReply,
    selectTicket,
    loadQueue,
    loadMyTickets,
    recentTickets,
    loadRecentTickets,
    changeRecentPeriod,
    sendAttachment,
    reopenTicket,
  };
}
